import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTION_TYPES = [
  { value: "feature", label: "Feature Film" },
  { value: "commercial", label: "Commercial" },
  { value: "episodic", label: "Episodic / TV" },
  { value: "music_video", label: "Music Video" },
  { value: "documentary", label: "Documentary" },
  { value: "branded", label: "Branded Content" },
  { value: "other", label: "Other" },
];

const CAPABILITIES = [
  "Aerial Cinematics",
  "VFX Support",
  "Set Designs",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  production_name: "",
  production_type: "feature",
  shoot_window: "",
  location: "",
  capabilities: [],
  project_details: "",
};

export const StudiosInquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleCap = (c) =>
    setForm((f) => ({
      ...f,
      capabilities: f.capabilities.includes(c)
        ? f.capabilities.filter((x) => x !== c)
        : [...f.capabilities, c],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.production_type || !form.project_details) {
      toast.error("Please fill in contact, email, production type, and details.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/studios/inquiries`, form);
      setSuccess(true);
      setForm(initial);
      toast.success("Inquiry received. One of our crew members will reply within one business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not send your inquiry. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      onSubmit={onSubmit}
      data-testid="studios-inquiry-form"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0b1310] via-black to-black p-6 md:p-10 tb-glow overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(61,192,175,0.18),transparent_60%)]" />
      <div className="relative">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              Production inquiry
            </h3>
            <p className="mt-2 text-zinc-400 text-sm leading-relaxed max-w-md">
              Shoot dates, scope, and deliverables. We'll come back with a quote and
              a crew plan.
            </p>
          </div>
          <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase text-[#3DC0AF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
            TGI Studios
          </span>
        </div>

        {success && (
          <div
            data-testid="studios-success"
            className="mt-6 flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200"
          >
            <Check className="h-4 w-4" />
            Inquiry received. One of our crew members will reply within one business day.
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Contact name" required>
            <input
              required
              type="text"
              value={form.name}
              onChange={update("name")}
              data-testid="studios-form-name"
              placeholder="Jane Director"
              className={inputCls}
            />
          </Field>
          <Field label="Email" required>
            <input
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              data-testid="studios-form-email"
              placeholder="you@production.com"
              className={inputCls}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              data-testid="studios-form-phone"
              placeholder="(431) 000-0000"
              className={inputCls}
            />
          </Field>
          <Field label="Production name">
            <input
              type="text"
              value={form.production_name}
              onChange={update("production_name")}
              data-testid="studios-form-production-name"
              placeholder="Working title"
              className={inputCls}
            />
          </Field>
          <Field label="Production type" required>
            <select
              required
              value={form.production_type}
              onChange={update("production_type")}
              data-testid="studios-form-type"
              className={`${inputCls} appearance-none pr-10`}
            >
              {PRODUCTION_TYPES.map((p) => (
                <option key={p.value} value={p.value} className="bg-black">
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Shoot window">
            <input
              type="text"
              value={form.shoot_window}
              onChange={update("shoot_window")}
              data-testid="studios-form-window"
              placeholder="e.g. Sep 15–20, 2026"
              className={inputCls}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Location">
              <input
                type="text"
                value={form.location}
                onChange={update("location")}
                data-testid="studios-form-location"
                placeholder="City / region / remote"
                className={inputCls}
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <div className="block text-[11px] tracking-[0.2em] uppercase font-mono text-zinc-500 mb-3">
              Capabilities needed
            </div>
            <div className="flex flex-wrap gap-2">
              {CAPABILITIES.map((c) => {
                const active = form.capabilities.includes(c);
                return (
                  <button
                    type="button"
                    key={c}
                    data-testid={`studios-cap-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleCap(c)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      active
                        ? "bg-[#3DC0AF] text-white border-[#3DC0AF]"
                        : "bg-zinc-900/60 text-zinc-300 border-white/10 hover:border-[#3DC0AF]/50"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <Field label="Project details" required>
              <textarea
                required
                rows={5}
                value={form.project_details}
                onChange={update("project_details")}
                data-testid="studios-form-details"
                placeholder="Scope, deliverables, references, budget range, any creative notes..."
                className={`${inputCls} resize-none`}
              />
            </Field>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-zinc-500 max-w-sm">
            We keep all production details strictly confidential.
          </p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="studios-form-submit"
            className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] disabled:opacity-60 text-white px-7 py-3.5 text-sm font-medium transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                Send inquiry
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </div>
      </div>
    </motion.form>
  );
};

const inputCls =
  "w-full rounded-lg bg-zinc-900/80 border border-white/10 text-white placeholder:text-zinc-600 px-4 py-3 text-sm outline-none focus:border-[#3DC0AF] focus:ring-2 focus:ring-[#3DC0AF]/30 transition-all";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.2em] uppercase font-mono text-zinc-500 mb-2">
      {label} {required && <span className="text-[#3DC0AF]">*</span>}
    </span>
    {children}
  </label>
);
